// Copyright 2025, University of Maryland, All Rights Reserved
import { useState } from 'react'

const INTENSITY_COLORS = {
  H: { bg: '#E21833', text: '#fff', label: 'High' },
  M: { bg: '#FFD200', text: '#000000', label: 'Medium' },
  L: { bg: '#D3D3D3', text: '#1a1a1a', label: 'Low' },
  null: { bg: '#fff', text: '#1a1a1a', label: 'Not addressed' }
}

const PROGRESSION_COLORS = {
  // UMD-aligned, higher-contrast but less harsh than full solid primaries.
  I: { bg: '#4B5563', text: '#FFFFFF', label: 'Introduced' },
  R: { bg: '#F4C300', text: '#111111', label: 'Reinforced' },
  M: { bg: '#C8102E', text: '#FFFFFF', label: 'Mastered' },
  null: { bg: '#fff', text: '#1a1a1a', label: 'Not addressed' }
}

const INTENSITY_BADGE_TEXT = {
  H: '#B10F28',
  M: '#6A4F00',
  L: '#4B5563'
}

interface ProgramLearningOutcome {
  id: number
  statement: string
  course_progression?: {
    label: string
    supporting_clo_ids?: number[]
    progression_level?: 'I' | 'R' | 'M' | null
    intensity?: 'H' | 'M' | 'L' | null
    progression_rationale?: string | null
  }[]
}

interface PLOCourseInstance {
  label: string
  coverage: {
    plo_id: number
    intensity: 'H' | 'M' | 'L' | null
    progression_level?: 'I' | 'R' | 'M' | null
    progression_rationale?: string | null
  }[]
}

export interface PLOHeatmapProps {
  program_learning_outcomes: ProgramLearningOutcome[]
  course_instances?: PLOCourseInstance[]
  summary?: string
}

export function PLOHeatmap({ program_learning_outcomes, course_instances: _courseInstances, summary }: PLOHeatmapProps) {
  const [tooltip, setTooltip] = useState<{
    x: number
    y: number
    docs: string
    level: string
    intensity: 'H' | 'M' | 'L' | null
    explanation?: string | null
  } | null>(null)

  const courseLabels: string[] = []
  const seenLabels = new Set<string>()
  program_learning_outcomes.forEach(plo => {
    ;(plo.course_progression ?? []).forEach(entry => {
      if (!entry?.label) return
      if (seenLabels.has(entry.label)) return
      seenLabels.add(entry.label)
      courseLabels.push(entry.label)
    })
  })

  const getEntry = (ploId: number, label: string) => {
    const plo = program_learning_outcomes.find(p => p.id === ploId)
    if (!plo) return null
    return (plo.course_progression ?? []).find(cp => cp.label === label) ?? null
  }

  const handleMouseEnter = (
    e: React.MouseEvent,
    data: { docs: string; level: string; intensity: 'H' | 'M' | 'L' | null; explanation?: string | null }
  ) => {
    const rect = (e.target as HTMLElement).getBoundingClientRect()
    setTooltip({ x: rect.left + rect.width / 2, y: rect.top - 8, ...data })
  }

  const handleMouseLeave = () => setTooltip(null)

  // Labels are a comma-joined list of filenames, but filenames themselves can
  // contain commas. Split ONLY at commas that immediately follow a file
  // extension (the true join boundaries); otherwise keep the string intact.
  const splitDocs = (s: string): string[] =>
    (s.match(/.+?\.[A-Za-z0-9]{2,5}(?=,\s|$)/g) ?? [s]).map(d => d.replace(/^[,\s]+/, ''))

  // Course codes are typically ~7-8 chars (e.g. KNES401). For a few courses, show
  // the first 8 chars horizontally (full label on hover). For many courses, switch
  // to vertical (rotated) headers with narrower columns so the map scrolls less.
  // First 8 chars, no ellipsis; drop any trailing separator (hyphen/underscore/space) left at the cut.
  const shortLabel = (l: string) => l.slice(0, 8).replace(/[-_\s]+$/, '')
  const manyCols = courseLabels.length > 8

  return (
    <div className="bg-white p-8 rounded-xl shadow-lg space-y-8 plo-heatmap-print">
      <div className="plo-heatmap-header">
        <h2 className="text-2xl font-bold text-gray-900 mb-1 plo-heatmap-title">Program Learning Outcomes – Curricular Map</h2>
        <p className="text-sm text-gray-500 plo-heatmap-meta">
          {program_learning_outcomes.length} PLOs · {courseLabels.length} course instances · I/R/M progression view
        </p>
      </div>

      {/* Heatmap grid */}
      <div className="overflow-x-auto plo-heatmap-scroller">
        <div
          className="inline-grid gap-px bg-gray-200 rounded-lg plo-heatmap-grid"
          style={{
            gridTemplateColumns: `minmax(240px, 1.5fr) repeat(${courseLabels.length}, minmax(${manyCols ? 44 : 64}px, 1fr))`,
          }}
        >
          {/* Header row (I/R/M progression) — sticky top-left corner */}
          <div className="sticky left-0 z-20 border-r border-gray-300 bg-gray-100 px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider plo-heatmap-header-cell plo-heatmap-header-cell-label">
            Program Learning Outcome (Progression)
          </div>
          {courseLabels.map((label, i) => (
            <div
              key={i}
              className={`bg-gray-100 text-xs font-semibold text-gray-700 plo-heatmap-header-cell plo-heatmap-col-header ${
                manyCols
                  ? 'flex items-end justify-center px-1 pt-3 pb-2'
                  : 'px-2 py-3 text-center truncate'
              }`}
              title={label}
            >
              {manyCols ? (
                <span
                  className="whitespace-nowrap leading-tight"
                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                >
                  {shortLabel(label)}
                </span>
              ) : (
                shortLabel(label)
              )}
            </div>
          ))}

          {/* Data rows */}
          {program_learning_outcomes.map(plo => (
            <>
              {/* PLO label cell — sticky first column so it stays visible while scrolling courses */}
              <div
                key={`plo-${plo.id}`}
                className="sticky left-0 z-10 border-r border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 flex items-start gap-2 plo-heatmap-label-cell"
              >
                <span className="text-xs font-bold text-gray-400 mt-0.5 w-5 shrink-0">
                  {plo.id}.
                </span>
                <span className="leading-snug plo-heatmap-label-text">{plo.statement}</span>
              </div>

              {/* Coverage cells */}
              {courseLabels.map((label, labelIdx) => {
                const entry = getEntry(plo.id, label)
                const intensity = entry?.intensity ?? null
                const progression = entry?.progression_level ?? null
                const progressionRationale = entry?.progression_rationale ?? null
                const colorCfg = PROGRESSION_COLORS[progression ?? 'null']

                return (
                  <div
                    key={`cell-${plo.id}-${labelIdx}`}
                    className="flex items-center justify-center py-3 px-2 cursor-default select-none relative plo-heatmap-value-cell"
                    style={{ backgroundColor: colorCfg.bg }}
                    onMouseEnter={e => handleMouseEnter(e, {
                      docs: label,
                      level: colorCfg.label,
                      intensity,
                      explanation: progressionRationale,
                    })}
                    onMouseLeave={handleMouseLeave}
                  >
                    {progression ? (
                      <span
                        className="text-sm font-bold plo-heatmap-value-text"
                        style={{ color: colorCfg.text }}
                      >
                        {progression}
                      </span>
                    ) : (
                      /* Diagonal stripe pattern for null */
                      <svg
                        className="w-6 h-6 opacity-20"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <line x1="0" y1="0" x2="24" y2="24" stroke="#666" strokeWidth="2" />
                        <line x1="12" y1="0" x2="24" y2="12" stroke="#666" strokeWidth="2" />
                        <line x1="0" y1="12" x2="12" y2="24" stroke="#666" strokeWidth="2" />
                      </svg>
                    )}
                    {intensity && (
                      <span
                        className="absolute top-1 right-1 w-5 h-5 rounded-full text-[10px] leading-none font-semibold border border-black/15 shadow-sm flex items-center justify-center plo-heatmap-badge"
                        style={{ backgroundColor: 'rgba(255,255,255,0.92)', color: INTENSITY_BADGE_TEXT[intensity] }}
                        title={`Intensity: ${INTENSITY_COLORS[intensity].label}`}
                      >
                        {intensity}
                      </span>
                    )}
                  </div>
                )
              })}
            </>
          ))}
        </div>
      </div>

      {/* Tooltip (portal-like fixed position) */}
      {tooltip && (
        <div
          className="fixed z-50 max-w-xs bg-gray-900 text-white text-xs rounded-lg px-3 py-2.5 shadow-xl pointer-events-none space-y-2"
          style={{
            left: tooltip.x,
            top: tooltip.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
              Document{splitDocs(tooltip.docs).length !== 1 ? 's' : ''}
            </div>
            <ul className="space-y-0.5 list-disc list-inside marker:text-gray-500">
              {splitDocs(tooltip.docs).map((d, i) => (
                <li key={i} className="leading-snug break-words">{d}</li>
              ))}
            </ul>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 border-t border-white/10">
            <div><span className="text-gray-400">Level:</span> {tooltip.level}</div>
            {tooltip.intensity && (
              <div>
                <span className="text-gray-400">Intensity:</span> {INTENSITY_COLORS[tooltip.intensity].label} ({tooltip.intensity})
              </div>
            )}
          </div>

          {tooltip.explanation && (
            <div className="pt-1 border-t border-white/10">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Explanation</div>
              <p className="leading-snug">{tooltip.explanation}</p>
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 plo-heatmap-legend">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Legend:</span>
        {(['I', 'R', 'M', 'null'] as const).map(key => {
          const cfg = PROGRESSION_COLORS[key]
          return (
            <div key={key} className="flex items-center gap-1.5 plo-heatmap-legend-item">
              <div
                className="w-6 h-6 rounded flex items-center justify-center border border-gray-200 plo-heatmap-legend-swatch"
                style={{ backgroundColor: cfg.bg }}
              >
                {key !== 'null' && (
                  <span className="text-xs font-bold" style={{ color: cfg.text }}>{key}</span>
                )}
              </div>
              <span className="text-xs text-gray-600">{cfg.label}</span>
            </div>
          )
        })}
      </div>

      {/* Summary */}
      {summary && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 plo-heatmap-summary">
          <h3 className="text-base font-semibold text-gray-800 mb-2">Summary</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{summary}</p>
        </div>
      )}
    </div>
  )
}
